import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableLazyLoadEvent } from 'primeng/table';
import { AdminService } from 'src/app/core/services/admin.service';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { ConfirmationModalComponent } from 'src/app/shared/components/confirmation-modal/confirmation-modal.component';
import { TableAction, TableColumn } from 'src/app/shared/models/table-config.model';
import { PermissionGroupEditModalComponent } from '../permission-group-edit-modal/permission-group-edit-modal.component';
import { AvailablePermission } from 'src/app/core/models/dto/availablePermission';

@Component({
  selector: 'app-permission-groups',
  templateUrl: './permission-groups.component.html',
  standalone: false,
  providers: [DialogService],
})
export class PermissionGroupsComponent implements OnInit {
  allGroups: any[] = [];
  tableData: any[] = [];
  totalRecords: number = 0;
  loading: boolean = false;
  searchText: string = '';
  availablePermissions: AvailablePermission[] = [];

  ref: DynamicDialogRef | undefined;

  tableColumns: TableColumn[] = [
    {
      field: 'name',
      header: 'GROUP',
      type: 'text',
      width: 'auto',
      sortable: true,
      linkAction: (row) => this.openEditModal(row),
    },
    {
      field: 'displayPermissions',
      header: 'PERMISSIONS',
      type: 'text',
      width: 'auto',
      sortable: false,
    },
    {
      field: 'userCount',
      header: 'USERS',
      type: 'text',
      width: '90px',
      sortable: true,
    },
    { field: 'actions', header: '', type: 'actions', width: 'auto' },
  ];

  tableActions: TableAction[] = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (row) => this.openEditModal(row),
      visible: (row) => !row.isSystem,
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      styleClass: 'p-button-text p-button-danger',
      command: (row) => this.deleteGroup(row),
      visible: (row) => !row.isSystem,
    },
  ];

  constructor(
    private adminService: AdminService,
    private readonly toastService: AppToastService,
    public dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.loadAvailablePermissions();
    this.loadGroups();
  }

  handleSearch(text: string) {
    this.searchText = text?.toLowerCase() || '';
    this.updateTableData();
  }

  loadGroups(): void {
    this.loading = true;
    this.adminService
      .getPermissionGroups()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (groups: any[]) => {
          this.allGroups = groups.map((g) => ({
            ...g,
            // Grupos de sistema (ex.: AdminGlobal) têm acesso total; evita listar todas as permissões.
            displayPermissions: g.isSystem
              ? 'Full Access'
              : (g.permissionNames || []).join(', '),
          }));
          this.totalRecords = this.allGroups.length;
          this.updateTableData();
        },
        error: () => {},
      });
  }

  loadAvailablePermissions(): void {
    this.adminService.getAvailableTenantPermissions().subscribe({
      next: (permissions) => {
        this.availablePermissions = permissions || [];
      },
      error: () => {},
    });
  }

  updateTableData(): void {
    const filtered = this.searchText
      ? this.allGroups.filter((g) =>
          [g.name, g.displayPermissions]
            .join(' ')
            .toLowerCase()
            .includes(this.searchText),
        )
      : [...this.allGroups];

    this.tableData = filtered;
    this.totalRecords = filtered.length;
  }

  loadLazy(event: TableLazyLoadEvent) {
    if (!event.sortField) {
      return;
    }

    const sorted = [...this.tableData].sort((a, b) => {
      const valueA = String(a[event.sortField as string] || '').toLowerCase();
      const valueB = String(b[event.sortField as string] || '').toLowerCase();
      return (event.sortOrder || 1) * valueA.localeCompare(valueB);
    });

    this.tableData = sorted;
  }

  openEditModal(group?: any): void {
    this.ref = this.dialogService.open(PermissionGroupEditModalComponent, {
      showHeader: false,
      width: '950px',
      styleClass: 'responsive-modal',
      contentStyle: {
        'max-height': '88vh',
        overflow: 'hidden',
        padding: '0',
        'border-radius': '12px',
      },
      data: {
        group,
        availablePermissions: this.availablePermissions,
        readOnly: !!group?.isSystem,
      },
    });

    this.ref.onClose.subscribe((result: any) => {
      if (!result) {
        return;
      }

      const normalizedName = (result.name || '').trim();
      if (!normalizedName) {
        this.toastService.warning('Validation', 'Group name is required.');
        return;
      }

      const payload = {
        name: normalizedName,
        permissionNames: result.permissionNames || [],
      };

      const operation = result.id
        ? this.adminService.updatePermissionGroup(result.id, payload)
        : this.adminService.createPermissionGroup(payload);

      operation.subscribe({
        next: () => {
          this.toastService.success('Saved', 'Permission group saved successfully.');
          this.loadGroups();
        },
        error: () => {},
      });
    });
  }

  deleteGroup(group: any): void {
    const ref = this.dialogService.open(ConfirmationModalComponent, {
      showHeader: false,
      width: '450px',
      contentStyle: { padding: '0', borderRadius: '8px' },
      data: {
        title: `Delete ${group.name}?`,
        message: 'Are you sure you want to delete this permission group? This action cannot be undone.',
        confirmLabel: 'Delete',
      },
    });

    ref.onClose.subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.adminService.deletePermissionGroup(group.id).subscribe({
        next: () => {
          this.toastService.success('Deleted', 'Permission group deleted successfully.');
          this.loadGroups();
        },
        error: () => {},
      });
    });
  }
}
